import { html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import {
  CardQueryGenerator,
  urlPathToNugetId,
  setPageMetadata,
} from '../functions';
import { StateController } from '../state';
import { CanOpenPackageElement } from '../can-open-package.element';

@customElement('umb-market-home')
export class UmbMarketHome extends CanOpenPackageElement {
  #cardDecks: Array<{
    query?: Record<string, any>;
    header: string;
    subheader: string;
  }> = [];

  #stateController: StateController;

  #featureText?: string;

  #bannerText = `<h2>Extend. Scale. Compose. Go.</h2>
    <p>
      Umbraco is built to be extendable. Here you'll find
      packages that add or extend functionality in the CMS,
      and integrations to the most popular technology and service vendors
      in the industry.
    </p>`;

  constructor() {
    super();
    this.#stateController = new StateController(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (StateController.current.isBackoffice) {
      this.setAttribute('data-backoffice', 'true');
    }

    this.#stateController.clear();

    this.#cardDecks = [
      {
        query: CardQueryGenerator('popular'),
        header: 'Most popular',
        subheader:
          'The number of installations reporting using the package within the last 14 days',
      },
      {
        query: CardQueryGenerator('recent'),
        header: 'Recently added',
        subheader: 'Packages recently added to Umbraco Marketplace',
      },
    ];

    if (StateController.variant === 'B') {
      this.#cardDecks.reverse();
    }

    // add this last, to avoid messing with A/B data - we are tracking
    // popular or recent first, don't want to add a third. It's not A/B/C testing.
    this.#cardDecks.push({
      query: CardQueryGenerator('updated'),
      header: 'Recently updated',
      subheader: 'Packages recently updated on Umbraco Marketplace',
    });

    if (StateController.isPackageView) {
      const packageId = urlPathToNugetId(window.location.pathname);
      if (packageId) {
        this.dispatchOpenModalEvent(packageId, true);
      }
    }

    setPageMetadata();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  // eslint-disable-next-line class-methods-use-this
  #getAwardYears() {
    const now = new Date();
    const currentYear = now.getFullYear();
    // Use last year until June of the current year (when new awards are announced)
    const year = now.getMonth() >= 5 ? currentYear : currentYear - 1;

    return [
      html`<umb-market-award-card title="Hall of Fame" path="hall-of-fame">
      </umb-market-award-card>`,
      ...Array.from({ length: year - 2020 }, (_, i) => year - i).map(
        y =>
          html`<umb-market-award-card
            title=${y}
            path=${y}
          ></umb-market-award-card>`
      ),
    ];
  }

  render() {
    return html`
      ${when(
        !StateController.current.isBackoffice,
        () => html` <umb-market-banner
          align="left"
          .text=${this.#bannerText}
          .featureText=${this.#featureText}
        ></umb-market-banner>`
      )}
      <umb-market-card-deck
        id="topCardDeck"
        header=${this.#cardDecks[0].header}
        subheader=${this.#cardDecks[0].subheader}
        .showAll=${true}
        .query=${this.#cardDecks[0].query}
      ></umb-market-card-deck>
      <umb-market-cta></umb-market-cta>
      <umb-market-card-deck
        header=${this.#cardDecks[1].header}
        subheader=${this.#cardDecks[1].subheader}
        .showAll=${true}
        .query=${this.#cardDecks[1].query}
      ></umb-market-card-deck>
      <umb-market-card-deck
        ?has-background=${true}
        header="Award winners"
        subheader="Explore award-winning Umbraco packages and extensions"
      >
        ${this.#getAwardYears()}
      </umb-market-card-deck>
      <umb-market-card-deck
        header=${this.#cardDecks[2].header}
        subheader=${this.#cardDecks[2].subheader}
        .showAll=${true}
        .query=${this.#cardDecks[2].query}
      ></umb-market-card-deck>
    `;
  }

  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: column;
      }

      umb-market-banner {
        margin-bottom: 0;
      }

      :host([data-backoffice]) #topCardDeck {
        --margin-top: 0;
      }
    `,
  ];
}
